import React, { useState } from 'react';
import { canModifyContent } from '@/lib/account-roles';
import {
  useAllVendorsQuery,
  useFestivalVendorsQuery,
  useRemoveFestivalVendorMutation,
  useSaveFestivalVendorMutation,
} from '@/queries/vendors';
import { useAuthStore } from '@/stores/auth-store';
import { useFestivalWorkspaceStore } from '@/stores/festival-workspace-store';
import { FestivalHeader, Loading } from '../controls';
import Portal from '../modal/Portal';
import FilterableTable from '../table';
import { FestivalVendorForm } from './';

export function FestivalVendors() {
  const currentUser = useAuthStore((s) => s.currentUser);
  const canModify = canModifyContent(currentUser);
  const festival = useFestivalWorkspaceStore((s) => s.festival);
  const festivalId = festival?.festivalId;

  const festivalVendorsQ = useFestivalVendorsQuery(festivalId);
  const allVendorsQ = useAllVendorsQuery();
  const saveMutation = useSaveFestivalVendorMutation(festivalId);
  const removeMutation = useRemoveFestivalVendorMutation(festivalId);

  const festivalVendors = festivalVendorsQ.data ?? [];
  const vendors = allVendorsQ.data ?? [];
  const loading =
    festivalId == null ||
    festivalVendorsQ.isPending ||
    festivalVendorsQ.isFetching ||
    allVendorsQ.isPending ||
    allVendorsQ.isFetching;

  const [selectedVendor, setSelectedVendor] = useState({});
  const [showModal, setShowModal] = useState(false);

  const buildColumnDefinitions = () => {
    var columnDefs = {};
    columnDefs.vendorName = {
      header: 'Vendor Name',
      filterable: true,
      sortable: true,
      showViewLink: false,
      handleClick: true,
    };
    columnDefs.category = {
      header: 'Category',
      filterable: false,
      sortable: false,
    };
    columnDefs.subcategory = {
      header: 'Subcategory',
      filterable: false,
      sortable: false,
    };
    return columnDefs;
  };

  const openModal = (vendor) => {
    if (!canModify) {
      return;
    }
    const next =
      festivalVendors.find((v) => v.vendor.vendorId === vendor.vendorId) || {};
    setSelectedVendor(next);
    setShowModal(true);
  };

  const onLocationChange = (location) => {
    const updatedLocation = {
      ...selectedVendor.location,
      ...location,
      radius: 20,
    };
    setSelectedVendor((prev) => ({
      ...prev,
      location: updatedLocation,
    }));
  };

  const onLocationNameChange = (locationName) => {
    const updatedLocation = {
      ...selectedVendor.location,
      locationName,
    };
    setSelectedVendor((prev) => ({
      ...prev,
      location: updatedLocation,
    }));
  };

  const handleSave = async (vendor) => {
    if (!canModify) {
      return;
    }
    const updatedVendor = { ...selectedVendor, ...vendor };
    try {
      await saveMutation.mutateAsync(updatedVendor);
      setShowModal(false);
      setSelectedVendor({});
    } catch {
      /* mutation toast */
    }
  };

  const handleRemove = async () => {
    if (!canModify) {
      return;
    }
    try {
      await removeMutation.mutateAsync(selectedVendor.vendorId);
      setShowModal(false);
      setSelectedVendor({});
    } catch {
      /* mutation toast */
    }
  };

  const vendorListData = festivalVendors.map((v) => v.vendor);

  return (
    <>
      <FestivalHeader
        title="Festival Vendors"
        showExit={true}
        showAdd={canModify}
        addTitle="Add Vendor"
        addAction={() => {
          if (!canModify) {
            return;
          }
          setSelectedVendor({});
          setShowModal(true);
        }}
      />
      {loading ? (
        <Loading />
      ) : (
        <FilterableTable
          data={vendorListData}
          tableTitle=""
          columns={buildColumnDefinitions()}
          includeFilter={false}
          editHandler={canModify ? openModal : undefined}
          toggleModal={canModify ? openModal : undefined}
        />
      )}

      {canModify && (
        <Portal
          header={
            selectedVendor.vendor
              ? selectedVendor.vendor.vendorName || 'Add Vendor'
              : 'Add Vendor'
          }
          open={showModal}
          onClose={() => setShowModal(false)}
        >
          <FestivalVendorForm
            vendor={selectedVendor}
            vendors={vendors}
            handleSave={handleSave}
            onLocationChange={onLocationChange}
            onLocationNameChange={onLocationNameChange}
            handleRemove={handleRemove}
          />
        </Portal>
      )}
    </>
  );
}
